import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'

export default function PartForm() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isEdit = !!id

  const [loading, setLoading] = useState(isEdit)
  const [errors, setErrors] = useState([])
  const [photoUrl, setPhotoUrl] = useState(null)
  const [uploadingPhoto, setUploadingPhoto] = useState(false)
  const [pendingPhoto, setPendingPhoto] = useState(null) // For new parts: file to upload after creation
  const [pendingPhotoPreview, setPendingPhotoPreview] = useState(null) // Preview URL for pending photo
  const fileInputRef = useRef(null)
  const [formData, setFormData] = useState({
    description: '',
    price: '',
    vendor_name: '',
    vendor_website: '',
    notes: '',
    quantity_in_stock: 0,
    reorder_threshold: 0
  })

  useEffect(() => {
    if (isEdit) {
      fetch(`/api/parts/${id}`)
        .then(response => response.json())
        .then(data => {
          setFormData({
            description: data.description || '',
            price: data.price || '',
            vendor_name: data.vendor_name || '',
            vendor_website: data.vendor_website || '',
            notes: data.notes || '',
            quantity_in_stock: data.quantity_in_stock ?? 0,
            reorder_threshold: data.reorder_threshold ?? 0
          })
          setPhotoUrl(data.photo_url)
          setLoading(false)
        })
        .catch(err => {
          console.error('Error fetching part:', err)
          setLoading(false)
        })
    }
  }, [id, isEdit])

  const handleChange = (e) => {
    const { name, value, type } = e.target
    setFormData({
      ...formData,
      [name]: type === 'number' ? (value === '' ? '' : parseFloat(value)) : value
    })
  }

  const handlePhotoSelect = (e) => {
    const file = e.target.files[0]
    if (!file) return

    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp']
    if (!allowedTypes.includes(file.type)) {
      alert('Please upload an image file (PNG, JPG, GIF, or WebP)')
      return
    }

    if (isEdit) {
      // For existing parts, upload immediately
      uploadPhoto(file)
    } else {
      // For new parts, store the file and show preview
      setPendingPhoto(file)
      setPendingPhotoPreview(URL.createObjectURL(file))
    }
  }

  const uploadPhoto = async (file, partId = id) => {
    setUploadingPhoto(true)
    const formDataUpload = new FormData()
    formDataUpload.append('file', file)

    try {
      const response = await fetch(`/api/parts/${partId}/upload_photo`, {
        method: 'POST',
        body: formDataUpload
      })

      const data = await response.json()
      if (data.success) {
        setPhotoUrl(data.photo_url)
        setPendingPhoto(null)
        setPendingPhotoPreview(null)
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
        return true
      } else {
        alert('Failed to upload photo: ' + (data.error || 'Unknown error'))
        return false
      }
    } catch (err) {
      console.error('Error uploading photo:', err)
      alert('Failed to upload photo')
      return false
    } finally {
      setUploadingPhoto(false)
    }
  }

  const clearPendingPhoto = () => {
    setPendingPhoto(null)
    if (pendingPhotoPreview) {
      URL.revokeObjectURL(pendingPhotoPreview)
      setPendingPhotoPreview(null)
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handlePhotoDelete = async () => {
    if (!confirm('Are you sure you want to delete this photo?')) return

    try {
      const response = await fetch(`/api/parts/${id}/delete_photo`, {
        method: 'DELETE'
      })

      const data = await response.json()
      if (data.success) {
        setPhotoUrl(null)
      } else {
        alert('Failed to delete photo: ' + (data.error || 'Unknown error'))
      }
    } catch (err) {
      console.error('Error deleting photo:', err)
      alert('Failed to delete photo')
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setErrors([])

    const url = isEdit ? `/api/parts/${id}` : '/api/parts'
    const method = isEdit ? 'PUT' : 'POST'

    try {
      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ part: formData })
      })

      const data = await response.json()
      if (data.errors) {
        setErrors(data.errors)
      } else {
        // If we have a pending photo for a new part, upload it now
        if (!isEdit && pendingPhoto && data.id) {
          await uploadPhoto(pendingPhoto, data.id)
        }
        navigate('/parts')
      }
    } catch (err) {
      console.error('Error saving part:', err)
      setErrors(['An unexpected error occurred'])
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    )
  }

  return (
    <div className="px-4 sm:px-0">
      <div className="mb-6">
        <Link to="/parts" className="text-gray-600 hover:text-gray-900">
          &larr; Back to Parts
        </Link>
      </div>

      <div className="bg-white shadow rounded-lg p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">
          {isEdit ? 'Edit Part' : 'Add New Part'}
        </h1>

        {errors.length > 0 && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <ul className="list-disc list-inside text-red-700">
              {errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Description */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description *
              </label>
              <input
                type="text"
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
              />
            </div>

            {/* Price */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Price *
              </label>
              <input
                type="number"
                name="price"
                value={formData.price}
                onChange={handleChange}
                required
                min="0"
                step="0.01"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
              />
            </div>

            {/* Quantity in Stock */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Quantity in Stock
              </label>
              <input
                type="number"
                name="quantity_in_stock"
                value={formData.quantity_in_stock}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
              />
            </div>

            {/* Reorder Threshold */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Order When Below
              </label>
              <input
                type="number"
                name="reorder_threshold"
                value={formData.reorder_threshold}
                onChange={handleChange}
                min="0"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
              />
            </div>

            {/* Vendor Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Vendor Name
              </label>
              <input
                type="text"
                name="vendor_name"
                value={formData.vendor_name}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
              />
            </div>

            {/* Vendor Website */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Vendor Website
              </label>
              <input
                type="url"
                name="vendor_website"
                value={formData.vendor_website}
                onChange={handleChange}
                placeholder="https://..."
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
              />
            </div>

            {/* Notes */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={4}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
              />
            </div>
          </div>

          {/* Photo Upload Section */}
          <div className="mt-6 border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Part Photo</h3>

            {/* Show existing photo (edit mode) */}
            {photoUrl ? (
              <div className="flex items-start space-x-4">
                <img
                  src={photoUrl}
                  alt={formData.description}
                  className="h-32 w-32 object-cover rounded-lg border"
                />
                <button
                  type="button"
                  onClick={handlePhotoDelete}
                  className="text-red-600 hover:text-red-900 text-sm"
                >
                  Delete Photo
                </button>
              </div>
            ) : pendingPhotoPreview ? (
              /* Show pending photo preview (create mode) */
              <div className="flex items-start space-x-4">
                <img
                  src={pendingPhotoPreview}
                  alt="Preview"
                  className="h-32 w-32 object-cover rounded-lg border"
                />
                <button
                  type="button"
                  onClick={clearPendingPhoto}
                  className="text-red-600 hover:text-red-900 text-sm"
                >
                  Remove Photo
                </button>
              </div>
            ) : (
              /* Show file input */
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoSelect}
                  disabled={uploadingPhoto}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
                />
                {uploadingPhoto && (
                  <p className="mt-2 text-sm text-gray-500">Uploading...</p>
                )}
                {!isEdit && (
                  <p className="mt-2 text-sm text-gray-500">Photo will be uploaded when you save the part.</p>
                )}
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className="mt-6 flex justify-end space-x-4">
            <Link
              to="/parts"
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900"
            >
              Cancel
            </Link>
            <button
              type="submit"
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900"
            >
              {isEdit ? 'Update Part' : 'Create Part'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
